import dayjs from 'dayjs';
import Decimal from 'decimal.js';
import { t } from 'i18next';
import { assoc } from 'ramda';
import * as yup from 'yup';
import { ObjectShape } from 'yup/lib/object';
import { DTRRuleConverted, RuleVariable } from '../../types';

const getRuleVariableShape = (variable: RuleVariable) => {
  switch (variable.ruleVariableName) {
    case 'nightShiftStartTime':
    case 'nightShiftEndTime':
    case 'nightShiftEndTimeRestThresh':
      return yup.date().required();
    case 'maxNightShiftsWeek52':
      return yup
        .number()
        .typeError(t('dtrRules.validation.required'))
        .required(t('dtrRules.validation.required'))
        .nullable()
        .transform((inputValue) => {
          return !inputValue ? null : new Decimal(inputValue).toNumber();
        });
    case 'maxWorkHoursInAvailabilityShift':
    case 'maxDutyHours':
    case 'maxAvailabilityShiftsWeek4':
    case 'maxDutyHoursWeek':
    case 'maxDutyHoursWeek4':
    case 'maxDutyHoursWeek16':
    case 'workHoursInAvailabilityShiftThresh':
    case 'maxConNightShifts':
    case 'maxNightShiftsWeek16':
    case 'minConRestHoursWeek':
    case 'minConRestHoursWeek2':
    case 'minConRestHoursWeek2Split':
    case 'minFreeSundaysWeek52':
    case 'minConHoursWithoutAvailabilityShiftWeek4':
    case 'dutyShortHoursThresh':
    case 'minHoursBreakDutyShort':
    case 'minHoursBreakDutyLong':
    case 'dutyLongHoursThresh':
    case 'minRestHoursAfter3OrMoreConNightShifts':
    case 'minRestHours':
    case 'minRestHoursExc':
    case 'minRestHoursAfterNightShift':
    case 'minRestHoursAfterNightShiftExc':
    case 'breakHoursThresh':
      return yup
        .number()
        .typeError(t('dtrRules.validation.required'))
        .required(t('dtrRules.validation.required'));
    default:
      return yup.mixed<unknown>().required(t('dtrRules.validation.required'));
  }
};

export const getSchema = (rowData: DTRRuleConverted) => {
  const rulesShape = rowData.variables.reduce(
    (acc, variable) =>
      assoc(
        variable.ruleVariableName,
        yup.object().shape({ value: getRuleVariableShape(variable) })
      )(acc),
    {}
  );

  return yup.object().shape<ObjectShape>({
    rules: yup
      .object()
      .shape(rulesShape)
      .test('start-end-time-validation', (_, ctx: yup.TestContext) => {
        const {
          parent: { rules }
        } = ctx;
        const { nightShiftStartTime, nightShiftEndTime } = rules || {};

        if (!nightShiftStartTime || !nightShiftEndTime) return true;

        const start = dayjs(nightShiftStartTime.value);
        const end = dayjs(nightShiftEndTime.value);

        // Invalidate if end comes after start
        if (start.isAfter(end)) {
          return ctx.createError({
            message: t('dtrRules.validation.startIsAfterEnd'),
            path: 'rules.nightShiftEndTime.value'
          });
        }

        // Invalidate if the difference between start and end is less than 23 hours and 59 minutes
        if (Math.abs(start.diff(end, 'ms')) > 1000 * 60 * 60 * 24 - 1000 * 60) {
          return ctx.createError({
            message: t('dtrRules.validation.moreThan24h'),
            path: 'rules.nightShiftEndTime.value'
          });
        }

        return true;
      })
  });
};
